/**
 * Loads Stripe.js on demand and initialises it with the platform publishable
 * key. Card data entered in the Stripe Element goes straight to Stripe and never
 * touches our server (PCI: we only ever handle a PaymentMethod id).
 *
 * The script is injected only when the billing panel actually needs it, so the
 * rest of the app carries no third-party payment code.
 */

// The Stripe.js global is loosely typed on purpose, we use a small, stable
// slice of it (elements, confirmCardSetup) and avoid pulling in the SDK types.
type StripeJs = {
  elements(options?: Record<string, unknown>): {
    create(type: string, options?: Record<string, unknown>): {
      mount(selectorOrEl: string | HTMLElement): void;
      unmount(): void;
      on(event: string, handler: (e: { error?: { message?: string } }) => void): void;
    };
  };
  confirmCardSetup(
    clientSecret: string,
    data: { payment_method: { card: unknown } },
  ): Promise<{
    error?: { message?: string };
    setupIntent?: { status: string; payment_method: string | null };
  }>;
};

declare global {
  interface Window {
    Stripe?: (publishableKey: string) => StripeJs;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.Stripe) return Promise.resolve();
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[src^="https://js.stripe.com/v3"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Stripe.js')));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export async function getStripe(publishableKey: string): Promise<StripeJs> {
  await loadScript();
  if (!window.Stripe) throw new Error('Stripe.js did not initialise');
  return window.Stripe(publishableKey);
}

export type { StripeJs };
